import { useCallback, useState } from 'react';
import { ControlProps } from '@jsonforms/core';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { withJsonFormsControlProps } from '@jsonforms/react';
import './TextControl.styles.scss';

const SECRET_PROPERTY_NAMES = ['cr_token', 'passphrase'];
const SECRET_LABEL_PATTERN = /token|passphrase|password|onepass/i;

/**
 * Plain text control (no use_endpoint). For string fields with use_endpoint, UseEndpointTextControl is used instead.
 */
export const TextControl = withJsonFormsControlProps((props: ControlProps) => {
    const { data, handleChange, path, label, required, errors, schema, uischema } = props;
    const [showPassword, setShowPassword] = useState(false);

    const schemaTitle = (schema as { title?: string })?.title ?? '';
    const fromPath = path?.split(/[/.]/).filter(Boolean).pop() ?? '';
    const scope = typeof uischema === 'object' && uischema != null && 'scope' in uischema ? (uischema as { scope?: string }).scope : undefined;
    const propertyName = fromPath || (scope?.split('/').filter(Boolean).pop() ?? '');

    const isSecretField =
        schema?.format === 'password' ||
        SECRET_PROPERTY_NAMES.includes(propertyName) ||
        (path?.includes('cr_token') || scope?.includes('cr_token') || path?.includes('passphrase') || scope?.includes('passphrase')) ||
        SECRET_LABEL_PATTERN.test(label ?? '') ||
        SECRET_LABEL_PATTERN.test(schemaTitle);

    const handleTextFieldChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(path, e.target.value);
        },
        [handleChange, path]
    );

    const inputType = isSecretField ? (showPassword ? 'text' : 'password') : 'text';
    const showError = Array.isArray(errors) ? errors.length > 0 : Boolean(errors);

    const endAdornment = isSecretField ? (
        <InputAdornment position="end">
            <IconButton
                aria-label={showPassword ? 'Hide value' : 'Show value'}
                onClick={() => setShowPassword((p) => !p)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                size="small"
            >
                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
        </InputAdornment>
    ) : undefined;

    return (
        <div className="rpc-text-control">
            <TextField
                value={data || ''}
                onChange={handleTextFieldChange}
                label={label}
                required={required}
                error={showError}
                helperText={errors as React.ReactNode}
                variant="outlined"
                fullWidth
                type={inputType}
                autoComplete={isSecretField ? 'off' : undefined}
                slotProps={{
                    input: {
                        required: false,
                        endAdornment,
                    },
                    htmlInput: {
                        type: inputType,
                        autoComplete: isSecretField ? 'off' : undefined,
                    },
                }}
            />
        </div>
    );
});
